import React, { useState, useEffect } from 'react';
import { collection, getDocs, orderBy, query, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import Dashboard from './Dashboard';
import './admin.css';

const Enquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      const q = query(collection(db, 'enquiries'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEnquiries(data);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateDoc(doc(db, 'enquiries', id), {
        status: newStatus,
        statusUpdatedAt: new Date().toISOString()
      });
      setEnquiries(enquiries.map(e => 
        e.id === id ? { ...e, status: newStatus } : e
      ));
      if (selectedEnquiry?.id === id) {
        setSelectedEnquiry({ ...selectedEnquiry, status: newStatus });
      }
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Seen': return '#3498db';
      case 'Action Taken': return '#f39c12';
      case 'Resolved': return '#27ae60';
      case 'Inactive': return '#95a5a6';
      case 'Disposed': return '#e74c3c';
      default: return '#7f8c8d';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      let date;
      // Handle Firestore timestamp
      if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle ISO string or regular date
      else if (typeof timestamp === 'string' || timestamp instanceof Date) {
        date = new Date(timestamp);
      } else {
        return 'N/A';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const filteredEnquiries = enquiries.filter(enquiry => 
    enquiry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    enquiry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    enquiry.service?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Dashboard>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading enquiries...</p>
        </div>
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      <div className="admin-page">
        <div className="page-header">
          <div className="header-info">
            <h2>Service Enquiries</h2>
            <p>Total: {enquiries.length} enquiries</p>
          </div>
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, email, or service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredEnquiries.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p>No enquiries found</p>
          </div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Service</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredEnquiries.map((enquiry) => (
                  <tr key={enquiry.id} onClick={() => setSelectedEnquiry(enquiry)}>
                    <td>{formatDate(enquiry.createdAt)}</td>
                    <td>{enquiry.name}</td>
                    <td>{enquiry.email}</td>
                    <td>{enquiry.phone}</td>
                    <td>
                      <span className="service-badge">{enquiry.service}</span>
                    </td>
                    <td>
                      <select
                        className="status-select"
                        value={enquiry.status || 'Pending'}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleStatusChange(enquiry.id, e.target.value);
                        }}
                        style={{ backgroundColor: getStatusColor(enquiry.status || 'Pending') }}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Seen">Seen</option>
                        <option value="Action Taken">Action Taken</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Disposed">Disposed</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail Modal */}
        {selectedEnquiry && (
          <div className="modal-overlay" onClick={() => setSelectedEnquiry(null)}>
            <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Enquiry Details</h3>
                <button className="close-btn" onClick={() => setSelectedEnquiry(null)}>×</button>
              </div>
              <div className="modal-body">
                <div className="detail-row">
                  <strong>Date:</strong>
                  <span>{formatDate(selectedEnquiry.createdAt)}</span>
                </div>
                <div className="detail-row">
                  <strong>Name:</strong>
                  <span>{selectedEnquiry.name}</span>
                </div>
                <div className="detail-row">
                  <strong>Email:</strong>
                  <span><a href={`mailto:${selectedEnquiry.email}`}>{selectedEnquiry.email}</a></span>
                </div>
                <div className="detail-row">
                  <strong>Phone:</strong>
                  <span><a href={`tel:${selectedEnquiry.phone}`}>{selectedEnquiry.phone}</a></span>
                </div>
                <div className="detail-row">
                  <strong>Service:</strong>
                  <span className="service-badge">{selectedEnquiry.service}</span>
                </div>
                <div className="detail-row">
                  <strong>Status:</strong>
                  <select
                    className="status-select-modal"
                    value={selectedEnquiry.status || 'Pending'}
                    onChange={(e) => handleStatusChange(selectedEnquiry.id, e.target.value)}
                    style={{ backgroundColor: getStatusColor(selectedEnquiry.status || 'Pending') }}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Seen">Seen</option>
                    <option value="Action Taken">Action Taken</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Disposed">Disposed</option>
                  </select>
                </div>
                {selectedEnquiry.message && (
                  <div className="detail-row message">
                    <strong>Message:</strong>
                    <p>{selectedEnquiry.message}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Dashboard>
  );
};

export default Enquiries;
